import { prepareReconciliationSet, runReconciliation } from "@/services/api";
import { reconciliationResultMessages } from "@/services/messages";
import { useTabActions } from "@/stores/tab-store";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, XCircle } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { toast } from "sonner";

/**
 * Failed-state отчёта (T059-T060, FR-020, FR-020a).
 *
 * Fullscreen блок с `XCircle` красной, заголовком, человекочитаемой причиной
 * (БЕЗ stack trace), тремя CTA: «Открыть журнал активности», «Попробовать снова»,
 * «Закрыть».
 *
 * Retry-цепочка (FR-020a):
 * 1. Клик «Попробовать снова» → диалог «Хотите подготовить набор заново?»
 * 2. «Подготовить заново» → переход на детали в edit-режим (закрывает failed-таб).
 * 3. «Запустить как есть» → POST /prepare → при ok автоматический POST /run +
 *    переключение в running-режим. При fail → возврат на детали с inline-issues.
 */
export interface FailedStateProps {
	setId: number;
	setName: string;
	runId: string;
	errorMessage: string | null;
	onClose?: () => void;
}

export function FailedState({ setId, setName, runId, errorMessage, onClose }: FailedStateProps) {
	const messages = reconciliationResultMessages.failed;
	const { openActivityLogTab, openReconciliationDetailTab, openReconciliationResultTab } =
		useTabActions();
	const queryClient = useQueryClient();
	const [retryDialogOpen, setRetryDialogOpen] = useState(false);

	const retryMutation = useMutation({
		mutationFn: async () => {
			const prepareResult = await prepareReconciliationSet(setId);
			if (!prepareResult.ok) {
				return { kind: "preflight-failed", issues: prepareResult.issues } as const;
			}
			const runResponse = await runReconciliation(setId);
			return { kind: "run-started", runResponse } as const;
		},
		onSuccess: (result) => {
			queryClient.invalidateQueries({ queryKey: ["reconciliation-set", setId] });
			queryClient.invalidateQueries({ queryKey: ["recon-runs", setId] });

			if (result.kind === "preflight-failed") {
				// FR-020a: при pre-flight fail возвращаемся на детали с inline-issues.
				openReconciliationDetailTab(setId, setName);
				return;
			}

			// run-started: открыть детали с активным прогрессом, либо result-таб для нового runId.
			if (result.runResponse.status === "started") {
				// Переключаем на детали — там RunProgressBar отрендерится через recon-runs.
				openReconciliationDetailTab(setId, setName);
			} else {
				// 409: открыть существующий result.
				openReconciliationResultTab(setId, result.runResponse.existingRunId, setName);
			}
		},
		onError: (err) => {
			const message = err instanceof Error ? err.message : String(err);
			toast.error(message);
		},
	});

	return (
		<>
			<section className="mx-auto max-w-3xl rounded-lg border border-red-200 bg-white px-6 py-12 text-center">
				<XCircle className="mx-auto h-12 w-12 text-red-500" aria-hidden="true" />
				<h2 className="mt-4 text-lg font-semibold text-red-900">{messages.title}</h2>
				{errorMessage && (
					<pre className="mx-auto mt-4 max-w-xl whitespace-pre-wrap rounded border border-red-200 bg-red-50 p-3 text-left text-xs text-red-800">
						{errorMessage}
					</pre>
				)}

				<div className="mt-6 flex flex-wrap items-center justify-center gap-2">
					<button
						type="button"
						onClick={openActivityLogTab}
						className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
					>
						{messages.open_logs_cta}
					</button>
					<button
						type="button"
						onClick={() => setRetryDialogOpen(true)}
						className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
					>
						{messages.retry_cta}
					</button>
					{onClose && (
						<button
							type="button"
							onClick={onClose}
							className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
						>
							{messages.close_cta}
						</button>
					)}
				</div>
				<p className="mt-3 text-[11px] text-slate-400 font-mono">runId: {runId}</p>
			</section>

			{retryDialogOpen && (
				<RetryDialog
					onPrepare={() => {
						setRetryDialogOpen(false);
						// FR-020a: «Подготовить заново» → переход на детали в edit-режим.
						openReconciliationDetailTab(setId, setName);
					}}
					onRunAsIs={() => {
						setRetryDialogOpen(false);
						retryMutation.mutate();
					}}
					onCancel={() => setRetryDialogOpen(false)}
					isPending={retryMutation.isPending}
				/>
			)}
		</>
	);
}

interface RetryDialogProps {
	onPrepare: () => void;
	onRunAsIs: () => void;
	onCancel: () => void;
	isPending: boolean;
}

function RetryDialog({ onPrepare, onRunAsIs, onCancel, isPending }: RetryDialogProps) {
	const messages = reconciliationResultMessages.failed;
	const titleId = useId();
	const dialogRef = useRef<HTMLDialogElement>(null);

	useEffect(() => {
		const el = dialogRef.current;
		if (el && !el.open) el.showModal();
	}, []);

	useEffect(() => {
		const el = dialogRef.current;
		if (!el) return;
		const handler = (e: Event) => {
			e.preventDefault();
			onCancel();
		};
		el.addEventListener("cancel", handler);
		return () => el.removeEventListener("cancel", handler);
	}, [onCancel]);

	return (
		<dialog
			ref={dialogRef}
			className="fixed inset-0 m-auto w-[calc(100%-2rem)] max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-lg bg-white p-6 shadow-xl backdrop:bg-black/50"
			aria-labelledby={titleId}
		>
			<h3 id={titleId} className="text-lg font-semibold text-slate-900">
				{messages.retry_dialog_title}
			</h3>
			<p className="mt-2 text-sm text-slate-600">{messages.retry_dialog_message}</p>
			<div className="mt-6 flex flex-wrap justify-end gap-2">
				<button
					type="button"
					onClick={onCancel}
					disabled={isPending}
					className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50"
				>
					{messages.retry_dialog_cancel}
				</button>
				<button
					type="button"
					onClick={onPrepare}
					disabled={isPending}
					className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50"
				>
					{messages.retry_dialog_prepare_cta}
				</button>
				<button
					type="button"
					onClick={onRunAsIs}
					disabled={isPending}
					className="inline-flex items-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
				>
					{isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
					{messages.retry_dialog_run_as_is_cta}
				</button>
			</div>
		</dialog>
	);
}
